import { useState, useEffect } from 'react';
import { printingService } from '../services/printingService';
import type { Report } from '../types/printing';
import { useAuth } from '../contexts/AuthContext';

export interface UseActiveReportsReturn {
  /** Aktive rapporter av gitt ReportType (tenant + delte master-maler, merged). */
  reports: Report[];
  /** Valgt rapport-ID (default = første aktive). undefined hvis ingen finnes. */
  selectedReportId: number | undefined;
  setSelectedReportId: (reportId: number | undefined) => void;
  loading: boolean;
}

/**
 * Laster tenantens aktive rapporter av en gitt type og holder styr på valgt
 * rapport — generalisering av useSsccReports (SortingBox-mønsteret): kalleren
 * sender `reportId` eksplisitt i stedet for at backend defaulter til legacy-ID.
 *
 * Auto-velger første aktive rapport; ved flere viser kalleren en dropdown.
 * Ved 0 rapporter forblir selectedReportId undefined — backend bruker da
 * DefaultReportId-innstillingen eller sin siste-utvei-default.
 */
export function useActiveReports(reportType: number): UseActiveReportsReturn {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.companyNo) return;
    let cancelled = false;
    setLoading(true);
    printingService
      .getReports(user.companyNo, reportType, true)
      .then((list) => {
        if (cancelled) return;
        setReports(list);
        setSelectedReportId((prev) => prev ?? (list.length > 0 ? list[0].reportID : undefined));
      })
      .catch((err) => {
        console.warn(`Kunne ikke laste rapporter av type ${reportType}:`, err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.companyNo, reportType]);

  return { reports, selectedReportId, setSelectedReportId, loading };
}
